import { useState, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { BrandLogo, useBrandLabel } from '../branding/BrandLogo'
import { TenantHeaderLabel } from '../branding/TenantHeaderLabel'
import { useNavigate, useLocation } from 'react-router'
import type { LucideIcon } from 'lucide-react'
import { WifiOff, RefreshCw, FileText, LayoutGrid } from 'lucide-react'
import { useGlobalSearchNav } from '../../hooks/useGlobalSearchNav'
import { DOCS_NAV_PATH } from '../../utils/globalSearchNav'
import { useAppMode } from '../../contexts/AppModeContext'
import { useActiveApp } from '../../contexts/ActiveAppContext'
import { AppSwitcher } from './AppSwitcher'
import { usePlatformSuffixHidden } from '../../contexts/UiCustomizationContext'
import { isAggregateTenantRoute } from '../../constants/aggregateTenantRoutes'
import { useTenant } from '../../contexts/TenantContext'
import { TenantSwitcher } from '../TenantSwitcher'
import { useIsFeatureEnabled } from '../../contexts/FeatureFlagsContext'
import { AppUserProfile } from './AppUserProfile'
import { useConnectionStatus } from '../../features/chat/hooks/useConnectionStatus'
import {
  AppHeaderWorkflowRunMetricsCrumbs,
  parseAgentWorkflowRunMetricsPath,
} from './AppHeaderWorkflowRunMetricsCrumbs'
import { BugReportButton } from '../../features/bug-report/BugReportButton'
import { isBugsnapUiEvent } from '../../features/bug-report/bugsnapUi'
import NotificationBell from '../../features/notifications/NotificationBell'
import { safeFetch } from '../../utils/validateFetchUrl'
import { logger } from '../../utils/logger'
import { useTenantHeaderLabel } from '../../hooks/useTenantHeaderLabel'
import { storeHeaderName } from './storeHeaderName'
import './AppHeader.css'

interface SearchDocItem {
  t: string      // Title
  u: string      // Relative route starting with /docs/
  b: string[]    // Breadcrumbs
}

interface AppNavResult {
  label: string
  path: string
  icon: LucideIcon
  group: string
}

function findHeaderScrollTarget(
  target: EventTarget | null,
  header: HTMLElement,
): HTMLElement | null {
  let el = target instanceof Node ? target : null
  while (el && el instanceof HTMLElement && header.contains(el)) {
    const { overflowY } = window.getComputedStyle(el)
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      el.scrollHeight > el.clientHeight + 1
    ) {
      return el
    }
    el = el.parentElement
  }
  return null
}

let searchIndexPromise: Promise<SearchDocItem[]> | null = null
let searchIndexErrorLogged = false

export function AppHeader() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { isMilkyway, isDataflow, isApps } = useAppMode()
  const { activeApp } = useActiveApp()
  const storeName = storeHeaderName(location.pathname, t)
  const headerAppName = isApps ? (storeName ?? activeApp?.name) : undefined
  const showStoreProduct = Boolean(isApps && storeName)
  // Announce the deployment's own brand, not the vendor wordmark.
  const platformBrandLabel = useBrandLabel('Platform')
  const milkywayBrandLabel = useBrandLabel()
  const appsBrandLabel = useBrandLabel(undefined, headerAppName)
  const suffixHidden = usePlatformSuffixHidden()
  const isDocsViewEnabled = useIsFeatureEnabled('docs_view')
  // Deployments that suppress the suffix show the brand name on its own.
  const brandSuffix = isApps || isMilkyway || suffixHidden ? undefined : 'Platform'
  const appNavItems = useGlobalSearchNav()
  const { isPlatformAdmin } = useTenant()
  const tenantHeaderLabel = useTenantHeaderLabel()
  const { status: connectionStatus, isConnected, forceReconnect } = useConnectionStatus()
  const [searchValue, setSearchValue] = useState('')
  const [searchFocused, setSearchFocused] = useState(false)
  const [searchIndex, setSearchIndex] = useState<SearchDocItem[]>([])
  const searchInputRef = useRef<HTMLInputElement>(null)
  const searchContainerRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLElement>(null)

  // Block wheel scroll from reaching the main page while the pointer is over the header.
  useEffect(() => {
    const header = headerRef.current
    if (!header) return

    const onWheel = (e: WheelEvent) => {
      const scrollable = findHeaderScrollTarget(e.target, header)

      if (scrollable) {
        const { scrollTop, scrollHeight, clientHeight } = scrollable
        const maxScroll = Math.max(0, scrollHeight - clientHeight)
        const atTop = scrollTop <= 0
        const atBottom = scrollTop + clientHeight >= scrollHeight - 1
        if ((e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom)) {
          e.preventDefault()
          return
        }
        scrollable.scrollTop = Math.max(0, Math.min(maxScroll, scrollTop + e.deltaY))
        e.preventDefault()
        return
      }

      e.preventDefault()
    }

    header.addEventListener('wheel', onWheel, { passive: false })
    return () => header.removeEventListener('wheel', onWheel)
  }, [])

  // Lightweight page metadata only (~tens of KB). Do not fetch the full lunr
  // search-index.json (~multi-MB) — proxies often truncate it mid-string.
  useEffect(() => {
    if (!searchIndexPromise) {
      searchIndexPromise = safeFetch('/product_docs/header-search-index.json')
        .then(async (response) => {
          if (response.ok) {
            const indexData: unknown = await response.json()
            return Array.isArray(indexData) ? (indexData as SearchDocItem[]) : []
          }
          return []
        })
        .catch((err) => {
          if (!searchIndexErrorLogged) {
            logger.warn('Failed to load documentation search index', err instanceof Error ? err : undefined)
            searchIndexErrorLogged = true
          }
          return []
        })
    }
    
    searchIndexPromise.then((documents) => {
      if (documents && documents.length > 0) {
        setSearchIndex(documents)
      }
    })
  }, [])

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Stay open while the user files a Bugsnap about this UI (see bugsnapUi).
      if (isBugsnapUiEvent(event.target, searchContainerRef.current)) return
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setSearchFocused(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Documentation results derived from the shared query string (top 5)
  const docResults = useMemo<SearchDocItem[]>(() => {
    const query = searchValue.toLowerCase().trim()
    if (!query || !searchIndex.length) return []

    const queryWords = query.split(/\s+/)
    return searchIndex
      .filter((doc) => {
        const title = (doc.t || '').toLowerCase()
        const breadcrumbs = (doc.b || []).join(' ').toLowerCase()
        return queryWords.every((word) => title.includes(word) || breadcrumbs.includes(word))
      })
      .slice(0, 5)
  }, [searchValue, searchIndex])

  // Application navigation results derived from the shared query string (top 5)
  const appResults = useMemo<AppNavResult[]>(() => {
    const query = searchValue.toLowerCase().trim()
    if (!query) return []

    const queryWords = query.split(/\s+/)
    return appNavItems
      .filter((item) => {
        const label = item.label.toLowerCase()
        const group = item.group.toLowerCase()
        return queryWords.every((word) => label.includes(word) || group.includes(word))
      })
      .slice(0, 5)
  }, [searchValue, appNavItems])

  const closeSearch = () => {
    setSearchValue('')
    setSearchFocused(false)
  }

  const handleAppResultClick = (item: AppNavResult) => {
    if (item.path === DOCS_NAV_PATH) {
      window.open('/product_docs/', '_blank', 'noopener,noreferrer')
    } else {
      navigate(item.path)
    }
    closeSearch()
  }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    closeSearch()
  }, [location.pathname])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        searchInputRef.current?.focus()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const workflowRunMetricsCrumbs = Boolean(parseAgentWorkflowRunMetricsPath(location.pathname))

  const baseBrandLabel = isApps
    ? appsBrandLabel
    : brandSuffix
      ? platformBrandLabel
      : milkywayBrandLabel
  const brandMarkLabel = tenantHeaderLabel
    ? `${baseBrandLabel} · ${tenantHeaderLabel}`
    : baseBrandLabel

  const goToHome = () => {
    if (isApps) {
      navigate('/')
      return
    }
    if (isMilkyway) navigate('/ideate')
    else if (isDataflow) {
      navigate('/ontology/migrations', { state: { openFirstGlobe: true } })
    } else navigate('/')
  }
  const brandTitle = isApps ? t('appSuites.switcher.allApps') : t('nav.goToHome')

  return (
    <header ref={headerRef} className={`top-bar${isApps && activeApp ? ' top-bar--apps' : ''}`}>
      <div className={`top-bar__left${workflowRunMetricsCrumbs ? ' top-bar__left--workflow-crumbs' : ''}`}>
        {isApps && !workflowRunMetricsCrumbs && <AppSwitcher />}
        {workflowRunMetricsCrumbs ? (
          <AppHeaderWorkflowRunMetricsCrumbs />
        ) : (
          <>
            {location.pathname.startsWith('/ideate') ? (
              <>
                <button
                  className="brand-mark"
                  aria-label={brandMarkLabel}
                  onClick={goToHome}
                  title={brandTitle}
                >
                  <BrandLogo size={17} suffix={brandSuffix} appName={headerAppName} />
                  {tenantHeaderLabel ? <TenantHeaderLabel label={tenantHeaderLabel} /> : null}
                </button>
                {!isMilkyway && (
                  <>
                    <span className="breadcrumb-separator">/</span>
                    <span className="page-title">{t('nav.autopilot')}</span>
                  </>
                )}
              </>
            ) : (
              <>
                <button
                  className="brand-mark"
                  aria-label={brandMarkLabel}
                  onClick={goToHome}
                  title={brandTitle}
                >
                  <BrandLogo size={17} suffix={brandSuffix} appName={headerAppName} />
                  {tenantHeaderLabel ? <TenantHeaderLabel label={tenantHeaderLabel} /> : null}
                </button>
                {!showStoreProduct && (
                  <>
                    <span className="breadcrumb-separator">/</span>
                    <span className="page-title" id="page-title" />
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
      <div className="top-bar__actions">
        {!isConnected && (
          <button
            className={`connection-status connection-status--${connectionStatus}`}
            onClick={forceReconnect}
            title={
              connectionStatus === 'reconnecting'
                ? t('common.connectionReconnecting')
                : t('common.connectionLostClickRetry')
            }
          >
            {connectionStatus === 'reconnecting' ? (
              <>
                <RefreshCw size={14} className="animate-spin" />
                <span>{t('common.connectionReconnecting')}</span>
              </>
            ) : (
              <>
                <WifiOff size={14} />
                <span>{t('common.connectionOffline')}</span>
              </>
            )}
          </button>
        )}

        {/* Tenant chip — platform admins only. Everyone else works inside a
            single tenant they cannot switch away from, so the chip carried no
            action and only added header noise. */}
        {isPlatformAdmin && location.pathname !== '/platform-admin/comparison' && (
          <TenantSwitcher compact showAggregateOption={isAggregateTenantRoute(location.pathname)} />
        )}

        <BugReportButton />

        <NotificationBell />

        <div ref={searchContainerRef} className="search-field-container">
          <div className={`search-field${searchValue || searchFocused ? ' expanded' : ''}`}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder={searchFocused ? t('common.searchExpandedPlaceholder') : t('common.searchPlaceholder')}
              aria-label={searchFocused ? t('common.searchExpandedPlaceholder') : t('common.searchPlaceholder')}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setSearchValue('')
                  setSearchFocused(false)
                  searchInputRef.current?.blur()
                }
              }}
            />
            <span className="kbd">⌘ K</span>

            {searchFocused && !!searchValue.trim() && (
              <div className="search-dropdown-overlay">

                    <div className="search-dropdown-section search-dropdown-section--app">
                      <div className="search-dropdown-header search-dropdown-header--app">
                        <LayoutGrid size={13} className="search-dropdown-header-icon" />
                        <span>{t('common.searchSectionApplication')}</span>
                      </div>
                      {appResults.length > 0 ? (
                        <ul className="search-results-list">
                          {appResults.map((item) => {
                            const ItemIcon = item.icon
                            return (
                              <li key={`${item.path}-${item.label}`} className="search-result-item">
                                <button
                                  type="button"
                                  className="search-result-link search-result-button"
                                  onClick={() => handleAppResultClick(item)}
                                >
                                  <ItemIcon size={14} className="search-result-doc-icon search-result-doc-icon--app" />
                                  <div className="search-result-details">
                                    <span className="search-result-title">{item.label}</span>
                                    {item.group && (
                                      <span className="search-result-breadcrumb">
                                        {item.group}
                                      </span>
                                    )}
                                  </div>
                                </button>
                              </li>
                            )
                          })}
                        </ul>
                      ) : (
                        <div className="search-dropdown-empty">
                          {t('common.searchNoFeatureResults')}
                        </div>
                      )}
                    </div>

                    {isDocsViewEnabled && (
                      <div className="search-dropdown-section search-dropdown-section--docs">
                        <div className="search-dropdown-header search-dropdown-header--docs">
                          <FileText size={13} className="search-dropdown-header-icon" />
                          <span>{t('common.searchSectionDocumentation')}</span>
                        </div>
                        {docResults.length > 0 ? (
                          <ul className="search-results-list">
                            {docResults.map((item) => {
                              const normalizedPath = item.u.startsWith('/docs/')
                                ? item.u.replace('/docs/', '/')
                                : item.u
                              const docUrl = `${normalizedPath}`

                              return (
                                <li key={`${item.u}-${item.t}`} className="search-result-item">
                                  <a
                                    href={docUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="search-result-link"
                                    onClick={closeSearch}
                                  >
                                    <FileText size={14} className="search-result-doc-icon" />
                                    <div className="search-result-details">
                                      <span className="search-result-title">{item.t}</span>
                                      {item.b && item.b.length > 0 && (
                                        <span className="search-result-breadcrumb">
                                          {item.b.join(' > ')}
                                        </span>
                                      )}
                                    </div>
                                  </a>
                                </li>
                              )
                            })}
                          </ul>
                        ) : (
                          <div className="search-dropdown-empty">
                            {t('common.searchNoDocResults')}
                          </div>
                        )}
                      </div>
                    )}
              </div>
            )}
          </div>
        </div>
        <AppUserProfile />
      </div>
    </header>
  )
}
